import { apiService } from './ApiService';
import lookupDataService from './data/LookupDataService';

export type ReportDataSource = 'inspectionResults' | 'inspectionPlans' | 'lineItems' | 'correctiveActions';

export interface ReportAttachment {
  attachmentId: number;
  fileId: number;
  filename: string;
  description: string;
  downloadUrl: string;
}

export interface InspectionResultRow {
  inspectionResultId: number;
  lineItemId: number;
  lineItem: string;
  group: string;
  title: string;
  siteName: string;
  siteId: number;
  inspectorName: string;
  status: string;
  timeSensitivity: string;
  inspectedOn: string;
  compositeScore: number | null;
  isExceptional: boolean;
  areaOfExpertise: string;
  checklistItemCount: number;
  correctiveActionCount: number;
  attachmentCount: number;
  // Raw attachment data for download action
  _attachments: ReportAttachment[];
}

export interface InspectionPlanRow {
  lineItemId: number;
  planId: number;
  lineItem: string;
  title: string;
  group: string;
  groupId: number;
  majorRequirement: string;
  isActive: boolean;
  isLocked: boolean;
  primaryValue: string;
  alternateValue: string;
  timeToInspect: string;
  sampleSize: string;
  scheduledInspectionsCount: number;
  checklistItemCount: number;
  lastUpdated: string;
  status: string;
  // Raw attachment data for download action
  _attachments: ReportAttachment[];
}

export interface LineItemRow {
  lineItemId: number;
  lineItem: string;
  title: string;
  group: string;
  groupIdentifier: string;
  majorRequirement: string;
  relatedInfo: string;
  performanceStandard: string;
  estimatedQuantity: string;
  isActive: boolean;
  modificationStatus: string;
  lastUpdated: string;
  attachmentCount: number;
  // Raw attachment data for download action
  _attachments: ReportAttachment[];
}

export interface CorrectiveActionRow {
  correctiveActionId: number;
  issue: string;
  lineItem: string;
  lineItemTitle: string;
  group: string;
  status: string;
  validationStatus: string;
  classification: string;
  site: string;
  issuedOn: string;
  issuedBy: string;
  acceptedOn: string;
  acceptedBy: string;
  lastResponseDate: string;
  lastResponseBy: string;
  lastResponseType: string;
  lastResponseComments: string;
  attachmentCount: number;
  _attachments: ReportAttachment[];
}

export type ReportRow = InspectionResultRow | InspectionPlanRow | LineItemRow | CorrectiveActionRow;

class ReportingService {

  public async loadInspectionResultRows(from?: string, to?: string): Promise<InspectionResultRow[]> {
    const groups = await apiService.getReportingInspectionResults(from, to);
    const rows: InspectionResultRow[] = [];

    for (const group of groups) {
      for (const lineItem of group.LineItems) {
        for (const result of lineItem.InspectionResults) {
          rows.push({
            inspectionResultId: result.Id,
            lineItemId: lineItem.Id,
            lineItem: lineItem.LineItem,
            group: group.Title,
            title: lineItem.Title,
            siteName: lookupDataService.getSiteNameById(result.SiteId) ?? '',
            siteId: result.SiteId,
            inspectorName: result.UserId ? (lookupDataService.getInspectorNameById(result.UserId) ?? '') : '',
            status: '',
            timeSensitivity: '',
            inspectedOn: result.InspectedOn ?? '',
            compositeScore: result.CompositeScore,
            isExceptional: result.IsExceptional ?? false,
            areaOfExpertise: lookupDataService.getExpertiseAreaNameById(lineItem.ExpertiseAreaId) ?? '',
            checklistItemCount: result.ChecklistItems?.length ?? 0,
            correctiveActionCount: result.CorrectiveActionCount,
            attachmentCount: result.AttachmentCount,
            _attachments: [],
          });
        }
      }
    }

    return rows;
  }

  public async loadInspectionPlanRows(): Promise<InspectionPlanRow[]> {
    const inspectionPlansResponse = await apiService.getInspectionPlans();
    const rows: InspectionPlanRow[] = [];

    for (const group of inspectionPlansResponse) {
      for (const lineItem of group.LineItems) {
        // Only include line items that have an inspection plan configured
        if (lineItem.InspectionMethodId == null && lineItem.ExpertiseAreaId == null && !lineItem.Status) {
          continue;
        }

        const attachments: ReportAttachment[] = [];
        for (const att of lineItem.Attachments || []) {
          if (att.FileId != null) {
            attachments.push({
              attachmentId: att.Id,
              fileId: att.FileId,
              filename: att.Filename || att.Description || `File ${att.FileId}`,
              description: att.Description || att.Filename || '',
              downloadUrl: apiService.generateAttachmentDownloadUrl(att.FileId),
            });
          }
        }

        rows.push({
          lineItemId: lineItem.Id,
          planId: lineItem.Id,
          lineItem: lineItem.LineItem,
          title: lineItem.Title,
          group: group.Title,
          groupId: group.Id,
          majorRequirement: lineItem.MajorRequirement,
          isActive: lineItem.IsActive,
          isLocked: lineItem.IsLocked ?? false,
          primaryValue: lineItem.PrimaryValue ?? '',
          alternateValue: lineItem.AlternateValue ?? '',
          timeToInspect: lineItem.TimeToInspect ?? '',
          sampleSize: lineItem.SampleSize ?? '',
          scheduledInspectionsCount: lineItem.ScheduledInspectionsCount ?? 0,
          checklistItemCount: lineItem.ChecklistItems?.length ?? 0,
          lastUpdated: lineItem.InspectionPlanLastUpdate ?? lineItem.LastUpdate ?? '',
          status: lineItem.Status ?? '',
          _attachments: attachments,
        });
      }
    }

    return rows;
  }

  public async loadLineItemRows(): Promise<LineItemRow[]> {
    const groups = await apiService.getContractLineItems();
    const rows: LineItemRow[] = [];

    for (const group of groups) {
      for (const lineItem of group.LineItems) {
        const attachments: ReportAttachment[] = [];
        for (const att of lineItem.Attachments || []) {
          if (att.FileId != null) {
            attachments.push({
              attachmentId: att.Id,
              fileId: att.FileId,
              filename: att.Filename || att.Description || `File ${att.FileId}`,
              description: att.Description || att.Filename || '',
              downloadUrl: apiService.generateAttachmentDownloadUrl(att.FileId),
            });
          }
        }
        for (const att of lineItem.LineItemAttachments || []) {
          if (att.FileId != null) {
            attachments.push({
              attachmentId: att.Id,
              fileId: att.FileId,
              filename: att.Filename || att.Description || `File ${att.FileId}`,
              description: att.Description || att.Filename || '',
              downloadUrl: apiService.generateAttachmentDownloadUrl(att.FileId),
            });
          }
        }

        rows.push({
          lineItemId: lineItem.Id,
          lineItem: lineItem.LineItem,
          title: lineItem.Title,
          group: group.Title,
          groupIdentifier: group.Identifier,
          majorRequirement: lineItem.MajorRequirement ?? '',
          relatedInfo: lineItem.RelatedInfo ?? '',
          performanceStandard: lineItem.PerformanceStandard ?? '',
          estimatedQuantity: lineItem.EstimatedQuantity ?? '',
          isActive: lineItem.IsActive,
          modificationStatus: lineItem.ModificationStatus ?? '',
          lastUpdated: lineItem.LineItemLastUpdate ?? lineItem.LastUpdate ?? '',
          attachmentCount: attachments.length,
          _attachments: attachments,
        });
      }
    }

    return rows;
  }

  public async loadCorrectiveActionRows(): Promise<CorrectiveActionRow[]> {
    const groups = await apiService.getContractCorrectiveActions();
    const classifications = lookupDataService.getClassifications();
    const rows: CorrectiveActionRow[] = [];

    for (const group of groups) {
      for (const lineItem of group.LineItems) {
        for (const inspectionResult of lineItem.InspectionResults || []) {
          for (const ca of inspectionResult.CorrectiveActions || []) {
            const attachments: ReportAttachment[] = [];
            for (const att of ca.Attachments || []) {
              if (att.FileId != null) {
                attachments.push({
                  attachmentId: att.Id,
                  fileId: att.FileId,
                  filename: att.Filename || att.Description || `File ${att.FileId}`,
                  description: att.Description || att.Filename || '',
                  downloadUrl: apiService.generateAttachmentDownloadUrl(att.FileId),
                });
              }
            }

            const classificationName = ca.ClassificationId != null
              ? (classifications?.get(ca.ClassificationId)?.Name ?? '')
              : '';

            rows.push({
              correctiveActionId: ca.Id,
              issue: ca.Issue ?? '',
              lineItem: lineItem.LineItem ?? '',
              lineItemTitle: lineItem.Title ?? '',
              group: group.Title ?? '',
              status: '',
              validationStatus: '',
              classification: classificationName,
              site: lookupDataService.getSiteNameById(ca.SiteId) ?? '',
              issuedOn: ca.Issued ?? '',
              issuedBy: ca.IssuedUserId ? (lookupDataService.getInspectorNameById(ca.IssuedUserId) ?? '') : '',
              acceptedOn: ca.Accepted ?? '',
              acceptedBy: ca.AcceptedBy ?? '',
              lastResponseDate: ca.LastResponseDate ?? '',
              lastResponseBy: ca.LastResponseBy ?? '',
              lastResponseType: ca.LastResponseType ?? '',
              lastResponseComments: ca.LastResponseComments ?? '',
              attachmentCount: attachments.length,
              _attachments: attachments,
            });
          }
        }
      }
    }

    return rows;
  }

  public async collectAttachments(selectedRows: ReportRow[]): Promise<ReportAttachment[]> {
    // For InspectionResultRows that have attachments but haven't been fetched yet, fetch on demand
    const inspectionRowsNeedingFetch = selectedRows.filter(
      (row): row is InspectionResultRow =>
        'inspectionResultId' in row &&
        (row as InspectionResultRow).attachmentCount > 0 &&
        (row as InspectionResultRow)._attachments.length === 0
    );

    if (inspectionRowsNeedingFetch.length > 0) {
      const ids = inspectionRowsNeedingFetch.map(r => r.inspectionResultId);
      const detailed = await apiService.getContractInspectionResultsForReport(ids);

      const attachmentMap = new Map<number, ReportAttachment[]>();
      for (const item of detailed) {
        for (const result of item.InspectionResults) {
          const attachments: ReportAttachment[] = [];
          for (const ci of result.ChecklistItems || []) {
            for (const att of ci.Attachments || []) {
              if (att.FileId && att.Filename) {
                attachments.push({
                  attachmentId: att.Id,
                  fileId: att.FileId,
                  filename: att.Filename,
                  description: att.Description || att.Filename,
                  downloadUrl: apiService.generateAttachmentDownloadUrl(att.FileId),
                });
              }
            }
          }
          attachmentMap.set(result.Id, attachments);
        }
      }

      // Mutate _attachments on rows so repeat downloads skip the fetch
      for (const row of inspectionRowsNeedingFetch) {
        row._attachments = attachmentMap.get(row.inspectionResultId) ?? [];
      }
    }

    const seen = new Set<string>();
    const result: ReportAttachment[] = [];
    for (const row of selectedRows) {
      for (const att of row._attachments) {
        const key = `${att.attachmentId}-${att.fileId}`;
        if (!seen.has(key)) {
          seen.add(key);
          result.push(att);
        }
      }
    }
    return result;
  }

  public triggerFileDownload(url: string, filename: string): void {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  public downloadAttachments(attachments: ReportAttachment[]): void {
    for (const att of attachments) {
      this.triggerFileDownload(att.downloadUrl, att.filename);
    }
  }

  public async downloadAttachmentsAsZip(attachments: ReportAttachment[], zipFilename: string = 'attachments'): Promise<void> {
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();

    // Track used filenames within the zip to avoid silent overwrites
    const usedNames = new Map<string, number>();
    const uniqueName = (filename: string): string => {
      const count = usedNames.get(filename) ?? 0;
      usedNames.set(filename, count + 1);
      if (count === 0) return filename;
      const dot = filename.lastIndexOf('.');
      return dot > -1
        ? `${filename.slice(0, dot)} (${count})${filename.slice(dot)}`
        : `${filename} (${count})`;
    };

    await Promise.all(
      attachments.map(async (att) => {
        const response = await fetch(att.downloadUrl);
        if (!response.ok) throw new Error(`Failed to fetch ${att.filename} (${response.status})`);
        const blob = await response.blob();
        zip.file(uniqueName(att.filename), blob);
      })
    );

    const content = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(content);
    const name = zipFilename.endsWith('.zip') ? zipFilename : `${zipFilename}.zip`;
    this.triggerFileDownload(url, name);
    URL.revokeObjectURL(url);
  }

  public validateFilename(name: string): string | null {
    if (!name || name.trim().length === 0) return 'Filename is required.';
    if (name.length > 100) return 'Filename must be 100 characters or fewer.';
    if (!/^[a-zA-Z0-9_\-. ]+$/.test(name)) return 'Filename may only contain letters, numbers, spaces, hyphens, underscores, and periods.';
    return null;
  }
}

export const reportingService = new ReportingService();
export default reportingService;
